import React, { useState } from 'react';
import { AlertTriangle, Check, CornerDownRight, Minus, X } from 'lucide-react';
import { SpecMet, SupplierView } from './discoveryModel';
import { firstMessageAsk, negotiationAngles, savingsLabel, worthAskingNote } from './discoveryInsights';

/**
 * Expanded supplier detail (v2.1 mock, "View details"): price & negotiation
 * room → spec match → compliance → track record → worth-asking note. Shared
 * verbatim by the pick cards and the other-suppliers accordion rows. Every
 * value traces to stored data; absent slots render an honest "—" and the
 * four track-record stats stay dashes until platform integrations land.
 */
interface SupplierDetailBlocksProps {
  supplier: SupplierView;
  quantity?: number;
  /** The buyer's stated compliance requirements (criteria step). */
  requiredCompliance?: string[];
  /** Persists the worth-asking note onto the supplier's outreach draft. */
  onAddToFirstMessage?: (note: string) => void;
}

const BlockHeader: React.FC<{ title: string; aside?: string }> = ({ title, aside }) => (
  <div className="mb-[7px] flex items-baseline gap-s3">
    <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">{title}</span>
    {aside && <span className="ml-auto text-[10.5px] text-ink-muted">{aside}</span>}
  </div>
);

const SPEC_ICONS: Record<SpecMet, React.ReactNode> = {
  met: <Check className="h-3 w-3 text-success-ink" />,
  exceeds: <Check className="h-3 w-3 text-success-ink" />,
  miss: <X className="h-3 w-3 text-danger-ink" />,
  unknown: <Minus className="h-3 w-3 text-ink-muted" />,
};

const TRACK_RECORD_STATS = ['Quotes given', 'On-time', 'Avg. reply', 'Warranty'];

export const SupplierDetailBlocks: React.FC<SupplierDetailBlocksProps> = ({
  supplier,
  quantity,
  requiredCompliance,
  onAddToFirstMessage,
}) => {
  const [addedToDraft, setAddedToDraft] = useState(false);
  const angles = negotiationAngles(supplier);
  const savings = savingsLabel(supplier, quantity);
  const note = worthAskingNote(supplier);
  const ask = firstMessageAsk(supplier);
  const specRows = supplier.specMatch ?? [];
  const specMetCount = specRows.filter((r) => r.met === 'met' || r.met === 'exceeds').length;

  return (
    <div className="mt-s4 flex flex-col gap-[14px] rounded-[9px] border border-line bg-surface-hover px-[14px] py-[12px]">
      <div>
        <BlockHeader title="Price & negotiation room" aside="Robbie's read on this quote" />
        <div className="mb-[9px] grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-s3">
          <div className="rounded-[7px] border border-line bg-surface px-[11px] py-[9px]">
            <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-ink-muted">They quoted</div>
            <div className="font-mono text-[16px] font-semibold leading-[1.3] text-ink">
              {supplier.quotedPrice != null ? `${supplier.currencySymbol}${supplier.quotedPrice.toLocaleString()}` : '—'}
            </div>
            {quantity != null && supplier.quotedPrice != null && (
              <div className="text-[10px] text-ink-muted">at your quantity of {quantity}</div>
            )}
          </div>
          <div className="rounded-[7px] border border-robbie-border bg-ai-chip px-[11px] py-[9px]">
            <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-ink-muted">Robbie thinks you can get</div>
            <div className="font-mono text-[16px] font-semibold leading-[1.3] text-robbie-ink">
              {supplier.negotiableTo?.price != null
                ? `${supplier.currencySymbol}${supplier.negotiableTo.price.toLocaleString()}`
                : '—'}
            </div>
            {savings ? (
              <div className="text-[10px] font-semibold text-success-ink">{savings}</div>
            ) : (
              supplier.negotiableTo && (
                <div
                  className="truncate text-[10px] text-ink-muted max-md:overflow-visible max-md:whitespace-normal"
                  title={supplier.negotiableTo.basis}
                >
                  {supplier.negotiableTo.basis}
                </div>
              )
            )}
          </div>
        </div>
        {angles.length > 0 && (
          <div className="flex flex-col gap-[5px]">
            {angles.map((line, i) => (
              <div key={i} className="flex items-start gap-[7px]">
                <CornerDownRight className="mt-[2px] h-3 w-3 shrink-0 text-robbie" />
                {/* Stated facts can run long (multi-sentence warranty text) —
                    cap at two lines, full text on hover. A phone has no hover,
                    so the cap there would be a deletion; it reads in full. */}
                <span className="line-clamp-2 text-meta leading-[1.5] text-ink-secondary max-md:line-clamp-none" title={line}>
                  {line}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <BlockHeader
          title="Spec match against your BOM line"
          aside={specRows.length > 0 ? `${specMetCount} of ${specRows.length} met` : undefined}
        />
        {specRows.length > 0 ? (
          <div>
            {specRows.map((row, i) => (
              <div
                key={i}
                // Requirement beside offer on a desktop; offer under
                // requirement on a phone. Two ellipsis columns of ~140px each
                // cut both halves of the comparison, which leaves a row that
                // cannot be read at all — and neither half comes back, because
                // there is nothing to hover.
                className="grid grid-cols-[14px_minmax(0,1fr)_minmax(0,1fr)] items-center gap-s3 border-b border-line-divider py-s2 last:border-b-0 max-md:grid-cols-[14px_minmax(0,1fr)] max-md:items-start max-md:gap-y-s1"
              >
                {SPEC_ICONS[row.met]}
                <span className="truncate text-meta text-ink-secondary max-md:overflow-visible max-md:whitespace-normal">
                  {row.requirement}
                </span>
                <span className="truncate text-meta font-semibold text-ink max-md:col-start-2 max-md:overflow-visible max-md:whitespace-normal">
                  {row.offered ?? '—'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="m-0 text-meta text-ink-muted">I couldn't compare this supplier against your spec line yet.</p>
        )}
      </div>

      <div>
        <BlockHeader
          title="Compliance"
          aside={
            requiredCompliance && requiredCompliance.length > 0
              ? `you required ${requiredCompliance.join(' + ')}`
              : undefined
          }
        />
        {supplier.compliance && supplier.compliance.length > 0 ? (
          <div className="flex flex-wrap gap-s2">
            {supplier.compliance.map((cert) => (
              <span
                key={cert.name}
                className={`inline-flex items-center gap-s1 rounded-pill border px-s3 py-[3px] text-[10.5px] font-semibold ${
                  cert.held === true
                    ? 'border-success-border bg-success-bg text-success-ink'
                    : cert.held === false
                      ? 'border-danger-border bg-danger-bg text-danger-ink'
                      : 'border-line bg-canvas text-ink-muted'
                }`}
              >
                {cert.held === true ? (
                  <Check className="h-[10px] w-[10px]" />
                ) : cert.held === false ? (
                  <X className="h-[10px] w-[10px]" />
                ) : (
                  <Minus className="h-[10px] w-[10px]" />
                )}
                {cert.name}
              </span>
            ))}
          </div>
        ) : (
          <p className="m-0 text-meta text-ink-muted">I couldn't find certification details for this supplier.</p>
        )}
      </div>

      <div>
        <BlockHeader title="Track record on Hello Robbie" />
        {/* Values stay em-dashes until platform integrations report real
            history — the slots exist so nothing shifts when they fill in. */}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(84px,1fr))] gap-s4">
          {TRACK_RECORD_STATS.map((label) => (
            <div key={label}>
              <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-ink-muted">{label}</div>
              <div className="font-mono text-row-title leading-[1.4] text-ink">—</div>
            </div>
          ))}
        </div>
      </div>

      {note && (
        <div className="rounded-control border border-warn-border bg-warn-bg px-[11px] py-[9px]">
          <div className="mb-[5px] flex items-center gap-s2">
            <AlertTriangle className="h-3 w-3 text-warn-ink" />
            <span className="text-[11px] font-bold text-warn-ink">Worth asking about</span>
          </div>
          <div className="text-meta leading-[1.55] text-ink-secondary">
            {note}
            {onAddToFirstMessage && ask && (
              <button
                type="button"
                onClick={() => {
                  onAddToFirstMessage(ask);
                  setAddedToDraft(true);
                }}
                disabled={addedToDraft}
                className="ml-s2 font-sans text-meta font-bold text-robbie disabled:text-ink-muted"
              >
                {addedToDraft ? 'Added to your first message ✓' : 'Add to my first message →'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierDetailBlocks;
